<template>
  <div class="relative">
    <div v-if="content" id="getting-started" class="text-white overflow-hidden max-w-screen pb-10">
      <h3 class="md:text-center pt-20 px-[20px]">
        {{ content.heading }}
      </h3>
      <div
        class="flex flex-col md:flex-row text-left gap-6 md:max-w-[1100px] w-full py-6 md:py-20 mx-auto relative z-20">
        <div class="px-[20px] z-30 relative">
          <div class="gap-y-10 intro-2">
            <div v-if="content.subHeading" v-html="content.subHeading" />
            <div v-if="content.tagline">
              {{ content.tagline }}
              <span
                class="underline cursor-pointer"
                v-if="content.taglineLink"
                @click="openHowWeWorkThisOutModal">
                {{ content.taglineLink }}
              </span>
            </div>
          </div>
          <div class="flex flex-col basis-1/3 flex-wrap gap-y-2 mt-[30px] z-30">
            <div class="w-full h-1px bg-blue-1 line-gradient" />
            <div
              v-for="(bullet, idx) in content.iterableItems"
              :key="idx"
              class="button-1 self-center md:self-start w-full">
              <BulletPoint :text="bullet.body" class="text-left pt-[4px]" fill="blue-2" />
              <div class="w-full h-1px bg-blue-1 line-gradient mt-[12px]" />
            </div>
          </div>
          <div v-if="content.footer" class="mt-[30px] paragraph-1">
            {{ content.footer }}
          </div>
          <OtivoButton
            class="w-full md:max-w-[360px] mt-10 max-sm:mx-auto"
            colour="white"
            @click="$emit('button-clicked')">
            {{ content.buttonText }}
          </OtivoButton>
        </div>
        <BetterOffPhone
          alt="BetterOffPhone"
          class="z-0 hidden xl:block w-full"
          :hand-hover-active="handHoverActive"
          @mouseover="handHover()"
          @mouseleave="handHover(false)" />
        <img
          :src="PointerHand"
          @mouseover="handHover()"
          @mouseleave="handHover(false)"
          alt="PointerHand"
          style="transform: scaleX(-1)"
          class="hidden xl:block absolute transition-all duration-300"
          :class="[
            handHoverActive ? '-bottom-[200px] -right-[380px]' : '-bottom-[260px] -right-[450px]',
          ]"
          id="pointer-hand" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import BulletPoint from '@/components/BulletPoint.vue'
import PointerHand from '@/components/SVGS/PointerHand.svg?url'
import OtivoButton from '@/components/OtivoButton.vue'
import BetterOffPhone from '@/components/BrandComponents/Otivo/Personal/BetterOffPhone.vue'
import { useDebounceFn } from '@vueuse/core'
import { nextTick, ref } from 'vue'
import { useModalStore } from '@/store/pinia/ModalStore.ts'
import HowWeWorkItOutModal from '@/components/BrandComponents/Otivo/HowWeWorkItOutModal.vue'

type ContentType = {
  heading: string
  subHeading?: string
  tagline?: string
  taglineLink?: string
  footer?: string
  buttonText: string
  iterableItems: Array<{ body: string }>
}

defineEmits(['button-clicked'])

interface Props {
  content: ContentType
}

defineProps<Props>()

const handHoverActive = ref(false)

const modalStore = useModalStore()

const openHowWeWorkThisOutModal = () => {
  modalStore.openModal(HowWeWorkItOutModal, {})
}

const handHover = useDebounceFn((value = true) => {
  nextTick(async () => {
    handHoverActive.value = value
  })
}, 10)
</script>

<style scoped>
.line-gradient {
  background: linear-gradient(90deg, #0037a1, #0037a1, #0064ff);
}
</style>
